import { saveAs } from "file-saver";
import { getDefectPhotoDownloadUrl } from "./mediaUrls.js";

// Photo slots as defined by the defect app's inspection form.
const PHOTO_SLOTS = {
    1: { label: "General arrangement", fileSuffix: "general_arrangement" },
    2: { label: "Close up", fileSuffix: "close_up" },
    3: { label: "Detailed", fileSuffix: "detailed" },
};

const PHOTO_INDEXES = Object.keys(PHOTO_SLOTS).map(Number);

const EXTENSION_BY_MIME = {
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/gif": "gif",
    "image/heic": "heic",
};

export function getPhotoLabel(index) {
    return PHOTO_SLOTS[index]?.label ?? `Photo ${index}`;
}

function sanitizeFilenamePart(value, fallback) {
    const safe = String(value ?? "")
        .replace(/[^A-Za-z0-9._-]+/g, "_")
        .replace(/^_+|_+$/g, "");
    return safe || fallback;
}

function extensionFromPath(path) {
    const match = /\.([A-Za-z0-9]+)$/.exec(String(path || ""));
    return match ? match[1].toLowerCase() : "";
}

function filenameFromContentDisposition(header) {
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header || "");
    return match ? match[1] : "";
}

/** Indexes (1-3) of the photos that exist for this defect. */
export function getDownloadablePhotoIndexes(defect) {
    if (!defect) return [];
    if (defect.submission_id) return PHOTO_INDEXES;
    return PHOTO_INDEXES.filter((index) => defect[`photo_${index}`]);
}

function buildFilename(defect, index, mimeType, dispositionFilename) {
    const stem = sanitizeFilenamePart(defect.defect_id, "defect");
    const mime = (mimeType || "").split(";")[0].trim().toLowerCase();
    // S3 objects are sometimes stored as octet-stream, so fall back to the
    // name the backend derived from the object key, then to the static
    // photo filename.
    const ext =
        EXTENSION_BY_MIME[mime] ||
        extensionFromPath(dispositionFilename) ||
        extensionFromPath(defect[`photo_${index}`]) ||
        "jpg";
    return `${stem}_${PHOTO_SLOTS[index].fileSuffix}.${ext}`;
}

async function fetchPhoto(url) {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }
    const contentType = (
        response.headers.get("Content-Type") || ""
    ).toLowerCase();
    // The dev server (and some static hosts) answer unknown paths with the
    // SPA index page instead of a 404, so never save an HTML page as a photo.
    if (contentType.includes("text/html")) {
        throw new Error("Photo not found");
    }
    return {
        blob: await response.blob(),
        dispositionFilename: filenameFromContentDisposition(
            response.headers.get("Content-Disposition"),
        ),
    };
}

export async function downloadDefectPhotos(defect) {
    const failed = [];
    for (const index of getDownloadablePhotoIndexes(defect)) {
        try {
            const { blob, dispositionFilename } = await fetchPhoto(
                getDefectPhotoDownloadUrl(defect, index),
            );
            saveAs(
                blob,
                buildFilename(defect, index, blob.type, dispositionFilename),
            );
        } catch (error) {
            console.error(
                `Failed to download photo ${index} for defect ${defect.defect_id}:`,
                error,
            );
            failed.push(index);
        }
    }
    return failed;
}
